import React, { useEffect, useState } from "react";
import {
    ButtonProps,
    CommandBarButton,
    createErrorFailure,
    DefaultButton,
    FormValidationSummary,
    LoadingButton,
    Modal,
    Stack,
    Text,
    validationFailed,
    ValidationResult
} from "sprint-common-web";

import { MovieResponse } from "../../../../common/api/catalogue-api/movie/MovieResponse";
import { MergeMoviesRequest } from "../../api/MergeMoviesRequest";
import { useAssetsManageMovieApi } from "../../api/useAssetsManageMovieApi";
import { mapMergeResponseToMovieItem, MovieItem } from "../MovieItem";

export interface MergeMoviesCommandBarButtonProps extends ButtonProps {
    moviesToMerge: MovieItem[];
    updateListAfterMerge(moviesToMerge: MovieItem[], newItem: MovieItem): void;
    clearMoviesToMerge(): void;
}

export const MergeMoviesCommandBarButton: React.FunctionComponent<MergeMoviesCommandBarButtonProps> = (
    props: MergeMoviesCommandBarButtonProps
) => {
    const { mergeMovies, validateMergeMovies } = useAssetsManageMovieApi();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isValidationLoading, setIsValidationLoading] = useState(false);
    const [validationResult, setValidationResult] = useState<ValidationResult>();

    useEffect(() => {
        setValidationResult(undefined);

        if (!isModalOpen) return;

        setIsValidationLoading(true);

        validateMergeMovies(createMergeRequest(props.moviesToMerge))
            .then((res) => setValidationResult(res))
            .catch((err) => setValidationResult(validationFailed([createErrorFailure(JSON.stringify(err))])))
            .finally(() => setIsValidationLoading(false));
    }, [isModalOpen]);

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const isMergeButtonDisabled = () => {
        return props.moviesToMerge.length < 2;
    };

    const handleMergeButtonClick = async (): Promise<void> => {
        setIsLoading(true);
        const response = await mergeMovies(createMergeRequest(props.moviesToMerge));
        props.updateListAfterMerge(props.moviesToMerge, createMovieItem(response.movie));
        closeModal();
        props.clearMoviesToMerge();
        setIsLoading(false);
    };

    const buttonDisabled = props.disabled || isMergeButtonDisabled();
    const isMergeConfirmDisabled = isLoading || isValidationLoading || !validationResult?.isValid;

    return (
        <>
            <CommandBarButton onClick={openModal} text="Merge" icon="Merge" disabled={buttonDisabled} />
            <Modal isBlocking={true} isOpen={isModalOpen} headerText="Merge Movies" onDismiss={closeModal}>
                <Stack tokens={{ childrenGap: 5 }}>
                    <Text>You are about to Merge:</Text>
                    {props.moviesToMerge.map((movie: MovieItem) => {
                        return <Text key={movie.id}>{movie.title}</Text>;
                    })}
                </Stack>
                <Stack tokens={{ padding: "10px 0" }}>
                    <FormValidationSummary validationResult={validationResult} isValidating={isValidationLoading} />
                </Stack>
                <Stack horizontal={true} tokens={{ childrenGap: 10 }} verticalAlign="end" horizontalAlign="end">
                    <DefaultButton text="Cancel" onClick={closeModal} disabled={isLoading} />
                    <LoadingButton
                        text="Merge"
                        isLoading={isLoading}
                        onClick={handleMergeButtonClick}
                        disabled={isMergeConfirmDisabled}
                    />
                </Stack>
            </Modal>
        </>
    );
};

const createMergeRequest = (movieItems: MovieItem[]): MergeMoviesRequest => {
    return {
        firstMovieId: movieItems[0].id,
        secondMovieId: movieItems[1].id
    };
};

const createMovieItem = (movieResponse: MovieResponse): MovieItem => ({
    ...mapMergeResponseToMovieItem(movieResponse),
    isModified: true
});
